import cx from 'classnames'
import React, { useContext } from 'react'
import { createContext } from 'react'

import SkeletonPlaceholder from '../skeleton-place-holder'
import styles from './table.module.scss'

export type TableProps<RowData, Column> = {
  loading?: boolean
  sticky?: boolean
  children: React.ReactNode
  tableData?: RowData[]
  columns: Column[]
  pageSize?: number
}

export type TableContextType<RowData, Column> = {
  tableData?: RowData[]
  columns: Column[]
  loading?: boolean
  pageSize?: number
}

function createTableContext<RowData extends object, Column extends object>() {
  return createContext<TableContextType<RowData, Column> | null>(null)
}

export const TableContext = createTableContext()

export function Table<RowData extends object, Column extends object>({
  children,
  sticky,
  pageSize = 15,
  tableData,
  columns,
  loading,
}: TableProps<RowData, Column>) {
  const value = { pageSize, tableData, columns, loading }

  return (
    <TableContext.Provider value={value}>
      <div className={cx(styles.tableWrapper)}>
        <table
          className={cx(styles.table, {
            [styles.tableSticky]: sticky,
          })}
        >
          {children}
        </table>
      </div>
    </TableContext.Provider>
  )
}

type TableElementProps = {
  className?: string
  children: React.ReactNode
}

type TableBodyProps = {
  className?: string
  children: React.ReactNode
}

export function TableHeader(
  props: TableElementProps & React.HTMLAttributes<HTMLTableSectionElement>,
) {
  return <thead className={cx(props.className)} {...props} />
}

export function TableBody({
  ...props
}: TableBodyProps & React.HTMLAttributes<HTMLTableSectionElement>) {
  const tableContext = useContext(TableContext)
  if (tableContext?.loading) {
    const tableData = Array(tableContext?.pageSize).fill({})
    return (
      <tbody>
        {tableData.map((_, i) => (
          <tr key={i}>
            {tableContext.columns?.map((_, i) => (
              <td key={i}>
                <SkeletonPlaceholder height="16px" width="174px" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    )
  }

  if (tableContext?.tableData?.length === 0)
    return (
      <tbody className={styles.emptyTable}>
        <tr>
          <td colSpan={tableContext?.columns.length}>
            {'No results returned'}
          </td>
        </tr>
      </tbody>
    )

  return <tbody className={cx(props.className)} {...props} />
}

export function TableRow(
  props: TableElementProps & React.HTMLAttributes<HTMLTableRowElement>,
) {
  return <tr className={cx(props.className)} {...props} />
}

export function TableHead(
  props: TableElementProps & React.ThHTMLAttributes<HTMLTableCellElement>,
) {
  return <th className={cx(props.className)} {...props} />
}

export function TableCell(
  props: TableElementProps & React.TdHTMLAttributes<HTMLTableCellElement>,
) {
  return <td className={cx(props.className)} {...props} />
}
